import * as yup from 'yup';

const lettersAndNumbersRegex = /^[a-zA-Z0-9 ]+$/;
const phoneRegex = /^[0-9\-()+\s]+$/;
const digitsOnlyRegex = /^\d+$/;
const validIntegerRegex = /^-?\d+$/;
const noSpecialCharsRegex = /^[a-zA-Z0-9\-\s)]+$/;
const validCurrency = /^[\d$.\s]+$/i;

export const mergeValidationSchemas = (...schemas) => {
  const [first, ...rest] = schemas;
  const merged = rest.reduce((mergedSchemas, schema) => mergedSchemas.concat(schema), first);
  return merged;
};

function validCreditCard(message) {
  return this.test('validCreditCard', message, (value) => {
    let cardNumber = value;

    // If the card number is null or empty, return false
    if (typeof cardNumber === 'undefined' || cardNumber?.length < 13) {
      return false;
    }

    // accept only digits, dashes or spaces
    if (/[^0-9-\s]+/.test(cardNumber)) return false;

    // not accepting diners club
    if (/^3[0,6,8]\d{12}$/.test(cardNumber)) return false;

    // "The Luhn Algorithm" to check if credit card number is valid
    let nCheck = 0;
    let nDigit = 0;
    let bEven = false;

    cardNumber = cardNumber.replace(/\D/g, '');

    for (let n = cardNumber.length - 1; n >= 0; n -= 1) {
      const cDigit = cardNumber.charAt(n);
      nDigit = parseInt(cDigit, 10);

      if (bEven) {
        if ((nDigit *= 2) > 9) nDigit -= 9;
      }

      nCheck += nDigit;
      bEven = !bEven;
    }

    return nCheck % 10 === 0;
  });
}

function customCharacterLengthMessage(length) {
  const message = `Please enter at least ${length} characters`;
  return this.test('customCharacterLengthMessage', message, (value) => !(value?.length < length));
}

function validExpiryDate(message) {
  return this.test('validExpiryDate', message, (value) => {
    const currentDate = new Date();
    const is2DigitYearFormat = /^\d{2}\/\d{2}$/.test(value); // 01/22
    const is4DigitYearFormat = /^\d{2}\/\d{4}$/.test(value); // 01/2022
    const isYearOnlyFormat = /^\d{4}$/.test(value); // 2022
    let expDay;
    let expMonth;
    let expYear;

    if (is2DigitYearFormat) {
      expMonth = value.substring(0, 2);
      expYear = 20 + value.slice(-2);
      expDay = new Date(expYear, expMonth);
    }

    if (is4DigitYearFormat) {
      expMonth = value.substring(0, 2);
      expYear = value.slice(-4);
      expDay = new Date(expYear, expMonth);
    }

    if (isYearOnlyFormat) {
      expMonth = value.substring(0, 2);
      expYear = 20 + value.slice(-2);
      expDay = new Date(expYear, expMonth);
    }

    return currentDate < expDay;
  });
}

function validPhoneNumber(message) {
  return this.test('validPhoneNumber', message, (value) => !value || phoneRegex.test(value));
}

function emptyOrNoSpecialChars(message) {
  return this.test(
    'emptyOrNoSpecialChars',
    message,
    (value) => !value || noSpecialCharsRegex.test(value)
  );
}

function emptyOrValidCurrency(message) {
  return this.test('emptyOrValidCurrency', message, (value) => {
    /**
     * Replacing non-numeric values with empty string does not work,
     * so we are replacing here before validation
     * Not allowed characters: non-numeric, e, E, +
     */
    const numericAndExponentialRegex = /^[0-9|e|E|\\+]+$/;
    const valueToTest = !numericAndExponentialRegex.test(value) ? '' : value;

    return !valueToTest || validCurrency.test(valueToTest);
  });
}

yup.addMethod(yup.string, 'validCreditCard', validCreditCard);
yup.addMethod(yup.string, 'validExpiryDate', validExpiryDate);
yup.addMethod(yup.string, 'validPhoneNumber', validPhoneNumber);
yup.addMethod(yup.string, 'emptyOrNoSpecialChars', emptyOrNoSpecialChars);
yup.addMethod(yup.string, 'customCharacterLengthMessage', customCharacterLengthMessage);
yup.addMethod(yup.string, 'emptyOrValidCurrency', emptyOrValidCurrency);

export const subscriptionFormSchema = yup.object().shape({
  firstName: yup
    .string()
    .trim()
    .required('First name is required')
    .matches(lettersAndNumbersRegex, 'Invalid characters are not allowed'),
  lastName: yup
    .string()
    .trim()
    .required('Last name is required')
    .matches(lettersAndNumbersRegex, 'Invalid characters are not allowed'),
  emailAddress: yup
    .string()
    .trim()
    .email('Please enter a valid email address')
    .required('Email address is required'),
});

export const searchFormSchema = yup.object().shape({
  inputSearch: yup
    .string()
    .trim()
    .required('Search input is a required field')
    .matches(lettersAndNumbersRegex, 'Search contains invalid characters'),
});

export const loginFormSchema = yup.object().shape({
  inputEmailSignin: yup
    .string()
    .trim()
    .email('Please enter a valid email address')
    .required('Email address is required'),
  inputPasswordSignin: yup.string().trim().required('Password is required'),
});
export const forgotPasswordFormSchema = yup.object().shape({
  emailAddress: yup
    .string()
    .trim()
    .email('Please enter a valid email address')
    .required('Email address is required'),
});
export const resetPasswordFormSchema = yup.object().shape({
  newInputPassword: yup
    .string()
    .trim()
    .required('New Password is required')
    .min(6, 'Password must be at least six characters long'),
});
export const registrationFormSchema = yup.object().shape({
  inputFirstNameContinue: yup
    .string()
    .trim()
    .required()
    .matches(lettersAndNumbersRegex, 'Invalid characters are not allowed'),
  inputLastNameContinue: yup
    .string()
    .trim()
    .required()
    .matches(lettersAndNumbersRegex, 'Invalid characters are not allowed'),
  inputEmailContinue: yup.string().trim().email('Please enter a valid email address').required(),
});

export const personalDetailsFormSchema = yup.object().shape({
  inputPhone: yup
    .string()
    .trim()
    .required('Phone Number Required')
    .matches(phoneRegex, 'Please enter a valid Contact Number'),
  inputPostcode: yup
    .string()
    .trim()
    .required('Post code Required')
    .matches(digitsOnlyRegex, 'Please enter a valid Postcode'),
});

export const cardPaymentFormSchema = yup.object().shape({
  inputCardName: yup
    .string()
    .trim()
    .required('Card Name Required')
    .matches(noSpecialCharsRegex, 'Invalid characters are not allowed'),
  inputCardNo: yup
    .string()
    .trim()
    .required('Credit Card Required')
    .max(16, 'Cannot be greater than 16 digits long')
    .matches(validIntegerRegex, 'Must be valid number')
    .validCreditCard('Invalid Card Number')
    .customCharacterLengthMessage(13),
  inputCardDate: yup
    .string()
    .trim()
    .required('Expiry Date Required')
    .validExpiryDate('Invalid Expiry Date')
    .min(7, 'Please enter in format MM/YYYY')
    .max(7, 'Invalid Format')
    .customCharacterLengthMessage(7),
  inputCardSecurity: yup
    .string()
    .trim()
    .required('CCV Required')
    .matches(validIntegerRegex, 'Must be valid number')
    .max(4, 'Cannot be greater than 4 digits long')
    .customCharacterLengthMessage(3),
});

export const termsAndConditionsSchema = yup.object().shape({
  checkboxTerms: yup
    .boolean()
    .required('This is a mandatory agreement')
    .oneOf([true], 'This is a mandatory agreement'),
});

const addressSchema = {
  deliveryAddress1: yup
    .string()
    .trim()
    .required('Address line 1 is required')
    .matches(noSpecialCharsRegex, 'Invalid characters are not allowed'),
  deliveryAddress2: yup
    .string()
    .trim()
    .nullable()
    .emptyOrNoSpecialChars('Invalid characters are not allowed'),
  suburb: yup
    .string()
    .trim()
    .required('Suburb is required')
    .matches(noSpecialCharsRegex, 'Invalid characters are not allowed'),
  postcode: yup
    .string()
    .trim()
    .required('Postcode is required')
    .matches(digitsOnlyRegex, 'Please enter a valid Postcode'),
};

export const deliveryAddressSchema = yup.object().shape(addressSchema);

const contactInformationSchema = {
  regFirstName: yup
    .string()
    .trim()
    .required('First name is required')
    .matches(noSpecialCharsRegex, 'Invalid characters are not allowed'),
  regLastName: yup
    .string()
    .trim()
    .required('Last name is required')
    .matches(noSpecialCharsRegex, 'Invalid characters are not allowed'),
  regPhoneNumber: yup
    .string()
    .trim()
    .nullable()
    .notRequired()
    .validPhoneNumber('Please enter a valid Phone Number'),
  regMobileNumber: yup
    .string()
    .trim()
    .nullable()
    .notRequired()
    .validPhoneNumber('Please enter a valid Phone Number'),
};

export const registrationFormCompleteSchema = yup.object().shape({
  ...contactInformationSchema,
  ...addressSchema,
  regInputEmail: yup
    .string()
    .trim()
    .required('Address line 1 is required')
    .email('Please enter a valid email address'),
  regInputPassword: yup
    .string()
    .trim()
    .required('Password is required')
    .min(6, 'Password must be at least six characters long'),
  regConfirmPassword: yup
    .string()
    .required('Confirm password is required')
    .min(6, 'Password must be at least six characters long')
    .oneOf([yup.ref('regInputPassword')], 'Passwords do not match'),
});

export const updateAccountInformationSchema = yup.object().shape({
  ...contactInformationSchema,
  ...addressSchema,
});

export const updateAccountEmailSchema = yup.object().shape({
  ...contactInformationSchema,
  ...addressSchema,
  updateEmailInputPassword: yup.string().trim().required('Password is required'),
  regInputEmail: yup
    .string()
    .trim()
    .required('Email address is required')
    .email('Please enter a valid email address'),
});

export const accountMembershipSchema = yup.object().shape({
  firstName: yup
    .string()
    .trim()
    .required()
    .matches(noSpecialCharsRegex, 'Invalid characters are not allowed'),
  lastName: yup
    .string()
    .trim()
    .required()
    .matches(noSpecialCharsRegex, 'Invalid characters are not allowed'),
  phoneNumber: yup.string().trim().required().validPhoneNumber('Please enter a valid Phone Number'),
});

export const changePasswordSchema = yup.object().shape({
  currentPassword: yup.string().trim().required('Old password is required'),
  newPassword: yup
    .string()
    .trim()
    .required('New password is required')
    .min(6, 'Password must be at least six characters long'),
});

export const emptySchema = yup.object().shape({});
export const giftVoucherFormSchema = yup.object().shape({
  OtherAmount: yup
    .number()
    .typeError('Please enter a valid number')
    .min(10, 'Value must be at least $10')
    .max(500, 'Value must not exceed $500'),
  EmailAddress: yup
    .string()
    .trim()
    .required('Email address is required')
    .email('Please enter a valid email address'),
  ConfirmEmailAddress: yup
    .string()
    .trim()
    .required('Email address needs to be confirmed')
    .email('Please enter a valid email address')
    .oneOf([yup.ref('EmailAddress')], 'Email addresses do not match'),
  Message: yup
    .string()
    .trim()
    .required('Message is required')
    .matches(noSpecialCharsRegex, 'Invalid characters are not allowed')
    .max(500, 'Message cannot exceed 500 characters'),
});

export const deliveryAddressInformationSchema = yup.object().shape({
  ...addressSchema,
  secondary: yup.array().of(yup.object().shape({ ...addressSchema })),
});
